import { Component, computed, inject, signal, TemplateRef, ViewChild } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { dateTimeFormat, dateTimeWithoutTimezone, formatAsUTC, monthOnlyFormat } from 'src/app/shared/utilities/date'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { DataTableComponent } from '../table/data-table.component'
import { ColDef, GetRowIdFunc } from 'ag-grid-community'
import { catchError, combineLatest, from, NEVER, switchMap, tap } from 'rxjs'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { withHeader } from '../table/headers/table-header.component'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { EntityFilterModalComponent } from '../modals/filters/entity-filter-modal.component'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { ToastService } from '../toasts/toasts.service'
import { MatMenuModule } from '@angular/material/menu'
import { TBillSettlementsService } from '../settlements/tbill-settlements.service'
import { PaginationParams } from 'src/app/shared/utilities/http-params'
import { TBillRetailerUsageOverrideCreateRequest, TBillRetailerUsageOverrideUpdateRequest, TBillRetailerUsageOverride } from '../settlements/tbill-settlements.types'
import { TablePaginationComponent } from '../table/table-pagination/table-pagination.component'
import { TableSortService } from '../table/services/table-sort.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { StringFilterModalComponent } from '../modals/filters/string-filter-modal.component'
import { NumberRangeFilter } from 'src/app/shared/services/filter/number-range-filter'
import { StringFilter } from 'src/app/shared/services/filter/string-filter'
import openModal from 'src/app/shared/services/openModal'
import { CreateTBillRetailerUsageOverrideModalComponent } from './create-tbill-retailer-usage-override-modal.component'
import { UpdateTBillRetailerUsageOverrideModalComponent } from './update-tbill-retailer-usage-override-modal.component'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { actionColumn, withCell } from '../table/cells/with-cell'
import { pipe } from 'lodash/fp'
import { NumberRangeFilterModalComponent } from '../modals/filters/number-range-filter-modal.component'
import { NumberCellComponent } from '../table/cells/number-cell.component'
import { DateCellComponent } from '../table/cells/date-cell.component'
import { EntityCellComponent } from '../table/cells/entity-cell.component'
import { DateDisplayComponent } from '../date-display/date-display.component'
import { NumberDisplayComponent } from '../number-display/number-display-component'

@Component({
  selector: 'app-tbill-retailer-usage-overrides',
  imports: [
    MatMenuModule,
    PageHeaderComponent,
    PageContentComponent,
    LabeledPropertyComponent,
    ActiveFilterListComponent,
    DataTableComponent,
    TablePaginationComponent,
    DateDisplayComponent,
    NumberDisplayComponent,
  ],
  providers: [TableSortService],
  styles: '',
  template: `
    <div app-page-header header="TBill Retailer Usage Overrides" [menuItems]="pageHeaderMenuItems"></div>
    <div app-page-content>
        <app-active-filter-list [filterGroup]="constructor.name"></app-active-filter-list>
        <app-data-table [loading]="loading()" [data]="data()" [getRowId]="getRowId" [columnDefs]="columnDefs"></app-data-table>
        <app-table-pagination
            [pageNumber]="pageNumber()"
            [pageSize]="pageSize()"
            [totalResults]="totalResults()"
            (pageNumberChange)="pageNumber.set($event)"
            (pageSizeChange)="pageSize.set($event)"
        />
    </div>

    <ng-template #deleteConfirmation let-ctx="ctx">
        Are you sure you want to delete TBill Retailer Usage Override?
        @if(ctx) {
            <div app-labeled-property label="Retailer">{{ctx.retailer_code}}</div>
            <div app-labeled-property label="Market Interval">
              <div app-date-display [value]="ctx.market_interval" [format]="monthOnlyFormat"></div>  
            </div>
            <div app-labeled-property label="Usage (kW\u00A0h)">
              <div app-number-display [value]="ctx.measurement_value"></div>
            </div>
        }
    </ng-template>
    `,
})
export class TBillRetailerUsageOverridesComponent {
  protected readonly monthOnlyFormat = monthOnlyFormat
  protected readonly dateTimeFormat = dateTimeFormat

  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly location = inject(Location)
  private readonly toastService = inject(ToastService)
  private readonly filterService = inject(FilterService)
  private readonly sortService = inject(TableSortService)
  private readonly modalService = inject(NgbModal)
  private readonly tbillSettlementsService = inject(TBillSettlementsService)

  @ViewChild('deleteConfirmation')
  deleteConfirmationTemplate: TemplateRef<{ ctx: TBillRetailerUsageOverride }>

  protected readonly refresh = signal(0)
  protected readonly loading = signal(false)
  protected readonly pageSize = signal(25)
  protected readonly pageNumber = signal(1)
  protected readonly totalResults = signal(0)
  protected readonly data = signal<TBillRetailerUsageOverride[]>([])
  protected readonly getRowId: GetRowIdFunc<TBillRetailerUsageOverride> = (data) => data.data.retailer_uid + formatAsUTC(data.data.market_interval)
  protected readonly paginationParams = computed<PaginationParams>(() => ({
    pageNumber: this.pageNumber(),
    pageSize: this.pageSize(),
  }))

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Create', onClick: () => this.openCreateModal() },
  ]

  protected readonly columnDefs: ColDef[] = [
    pipe(
      withHeader<TBillRetailerUsageOverride, EntityFilterModalComponent>({
        headerName: 'Retailer',
        field: 'retailer_code',
        filterable: true,
        sortable: true,
        filterGroup: TBillRetailerUsageOverridesComponent.name,
        modal: EntityFilterModalComponent,
        modalInit: {},
      }),
      withCell<EntityCellComponent<TBillRetailerUsageOverride, 'retailer_code'>>(
        EntityCellComponent,
        {
          label: (d) => d.retailer_name,
          value: (d) => d.retailer_code,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 150,
    }),
    pipe(
      withHeader<TBillRetailerUsageOverride, DateRangeFilterModalComponent>({
        headerName: 'Market Interval',
        field: 'market_interval',
        filterable: true,
        sortable: true,
        filterGroup: TBillRetailerUsageOverridesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillRetailerUsageOverride>>(
        DateCellComponent,
        {
          date: (d) => d.market_interval,
          format: monthOnlyFormat,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillRetailerUsageOverride, NumberRangeFilterModalComponent>({
        headerName: 'Usage (kW\u00A0h)',
        field: 'measurement_value',
        filterable: true,
        sortable: true,
        filterGroup: TBillRetailerUsageOverridesComponent.name,
        modal: NumberRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<NumberCellComponent<TBillRetailerUsageOverride>>(
        NumberCellComponent,
        {
          num: (d) => d.measurement_value,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 180,
    }),
    pipe(
      withHeader<TBillRetailerUsageOverride, DateRangeFilterModalComponent>({
        headerName: 'Created On (STL)',
        field: 'create_time',
        filterable: true,
        sortable: true,
        filterGroup: TBillRetailerUsageOverridesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillRetailerUsageOverride>>(
        DateCellComponent,
        {
          date: (d) => d.create_time,
          format: dateTimeWithoutTimezone,
          viewAsTZ: 'America/Chicago',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillRetailerUsageOverride, StringFilterModalComponent>({
        headerName: 'Created By',
        field: 'created_by',
        filterable: true,
        sortable: true,
        filterGroup: TBillRetailerUsageOverridesComponent.name,
        modal: StringFilterModalComponent,
        modalInit: {},
      }),
    )({
      editable: false,
      minWidth: 100,
      width: 200,
    }),
    pipe(
      withHeader<TBillRetailerUsageOverride, DateRangeFilterModalComponent>({
        headerName: 'Last Updated (STL)',
        field: 'last_update_time',
        filterable: true,
        sortable: true,
        filterGroup: TBillRetailerUsageOverridesComponent.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillRetailerUsageOverride>>(
        DateCellComponent,
        {
          date: (d) => d.last_update_time,
          format: dateTimeWithoutTimezone,
          viewAsTZ: 'America/Chicago',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillRetailerUsageOverride, StringFilterModalComponent>({
        headerName: 'Last Updated By',
        field: 'last_updated_by',
        filterable: true,
        sortable: true,
        filterGroup: TBillRetailerUsageOverridesComponent.name,
        modal: StringFilterModalComponent,
        modalInit: {},
      }))({
      editable: false,
      minWidth: 100,
      width: 200,
    }),
    actionColumn<TBillRetailerUsageOverride>(TBillRetailerUsageOverridesComponent.name, {
      actions: [
        {
          name: 'Update',
          onAction: (d: TBillRetailerUsageOverride) => this.openUpdateModal(d),
        },
        {
          name: 'Delete',
          onAction: (d: TBillRetailerUsageOverride) => this.openDeleteModal(d),
        },
      ],
    }),
  ]

  constructor() {
    const { dispatch } = this.filterService
    dispatch(FilterService.InitFiltersAction({
      fieldGroup: TBillRetailerUsageOverridesComponent.name,
      fields: {
        market_interval: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            sendTime: true,
            onlyMonth: true,
            meta: {
              fieldGroup: TBillRetailerUsageOverridesComponent.name,
              fieldName: 'market_interval',
              fieldDisplay: 'Market Interval',
            },
          },
        }, dispatch),
        retailer_code: new EntityFilter({
          config: {
            resource: 'retailers',
            allowMultiple: true,
            allowWildcards: false,
            meta: {
              fieldGroup: TBillRetailerUsageOverridesComponent.name,
              fieldName: 'retailer_code',
              fieldDisplay: 'Retailer',
            },
          },
        }, dispatch),
        measurement_value: new NumberRangeFilter({
          config: {
            meta: {
              fieldGroup: TBillRetailerUsageOverridesComponent.name,
              fieldName: 'measurement_value',
              fieldDisplay: 'Usage (kW\u00A0h)',
            },
            allowRange: true,
            precision: 8,
          },
        }, dispatch),
        create_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: {
              fieldGroup: TBillRetailerUsageOverridesComponent.name,
              fieldName: 'create_time',
              fieldDisplay: 'Created On (STL)',
            },
          },
        }, dispatch),
        created_by: new StringFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: TBillRetailerUsageOverridesComponent.name,
              fieldName: 'created_by',
              fieldDisplay: 'Created By',
            },
          },
        }, dispatch),
        last_update_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: {
              fieldGroup: TBillRetailerUsageOverridesComponent.name,
              fieldName: 'last_update_time',
              fieldDisplay: 'Last Updated (STL)',
            },
          },
        }, dispatch),
        last_updated_by: new StringFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: TBillRetailerUsageOverridesComponent.name,
              fieldName: 'last_updated_by',
              fieldDisplay: 'Last Updated By',
            },
          },
        }, dispatch),
      },
    }))

    combineLatest([
      toObservable(this.paginationParams),
      this.filterService
        .asParams(TBillRetailerUsageOverridesComponent.name)
        .pipe(
          tap(() => {
            if (this.pageNumber() !== 1) {
              this.pageNumber.set(1)
            }
          }),
        ),
      this.sortService.getOrder(),
      toObservable(this.refresh),
    ])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loading.set(true)),
        switchMap(([pagination, filters, order]) => this.tbillSettlementsService.getTBillRetailerUsageOverrides(pagination, filters, order)),
        catchError(this.handleLoadingError('Something went wrong loading tbill retailer usage overrides!')),
      )
      .subscribe({
        error:  this.handleLoadingError('Something went wrong loading tbill retailer usage overrides!'),
        next: (res) => {
          this.totalResults.set(res.total)
          this.data.set(res.results)
          this.loading.set(false)
        },
      })

    from(this.filterService.manageQueryParms(
      this.router,
      this.route,
      this.location,
      TBillRetailerUsageOverridesComponent.name,
    ))
      .pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  protected handleLoadingError(defaultMessage: string){
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e: any) => {
      this.data.set([])
      this.loading.set(false)
      this.toastService.show({ type: 'error', message: e.detail ?? defaultMessage })
      return NEVER
    }
  }

  async openCreateModal() {
    openModal(
      this.modalService,
      CreateTBillRetailerUsageOverrideModalComponent,
      { centered: true },
    ).init({
      modalState: {},
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onCreate: this.onCreate.bind(this),
    })
  }

  async onCreate(req: TBillRetailerUsageOverrideCreateRequest) {
    this.tbillSettlementsService.createTBillRetailerUsageOverride(req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong creating this override!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Creation successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  async openUpdateModal(data: TBillRetailerUsageOverride) {
    openModal(
      this.modalService,
      UpdateTBillRetailerUsageOverrideModalComponent,
      { centered: true },
    ).init({
      data,
      modalState: {
        measurement_value: data.measurement_value,
      },
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onUpdate: this.onUpdate.bind(this),
    })
  }

  async onUpdate(retailer_uid: string, market_interval: Date, req: TBillRetailerUsageOverrideUpdateRequest) {
    this.tbillSettlementsService.updateTBillRetailerUsageOverride(retailer_uid, market_interval, req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong updating this override!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Update successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  async openDeleteModal(data: TBillRetailerUsageOverride) {
    openModal(
      this.modalService,
      ConfirmationModalComponent,
      { centered: true },
    ).init({
      headerText: 'Confirm Deletion',
      confirmButtonText: 'Delete',
      bodyTemplate: this.deleteConfirmationTemplate,
      bodyContext: data,
      onCancel: () => {
        this.modalService.dismissAll()
      },
      onConfirm: () => {
        this.onDelete(data.retailer_uid, data.market_interval)
      },
    })
  }

  async onDelete(retailer_uid: string, market_interval: Date) {
    this.tbillSettlementsService.deleteTBillRetailerUsageOverride(retailer_uid, market_interval)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong deleting this override!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Deletion successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }
}